import * as amplitude from "@amplitude/analytics-browser";
import { GeneralPermissions } from "@redotech/account-common/permissions";
import { isOAuthEmailIntegrationType } from "@redotech/effect-api-shared/models/shopper-ai/conversation/channel-utils";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useIntegrations } from "@redotech/redo-merchant-support-app/v2/hooks/use-integrations";
import { NotificationBannerItem } from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PlatformEvents } from "../../../platform-events";
import { ActionRequiredSeverity } from "./action-required-severity";

export function useRevokedEmailPermissions(): {
  severity: ActionRequiredSeverity;
  item: NotificationBannerItem;
} | null {
  const navigate = useNavigate();
  const team = useContext(TeamContext);
  const { data: integrations, isLoading } = useIntegrations();

  const canAuthenticateWithThirdParties = useHasPermission(
    GeneralPermissions.AUTHENTICATE_WITH_THIRD_PARTIES,
  );

  const revokedIntegrationInfo = useMemo(() => {
    if (!team?.settings.support?.enabled || isLoading) {
      return { hasRevoked: false, emailType: null };
    }

    const revokedOAuthIntegrations = (integrations ?? []).filter(
      (integration) =>
        isOAuthEmailIntegrationType(integration.integrationType) &&
        integration.channelData &&
        integration.permissionsRevoked,
    );

    const hasRevokedGmail = revokedOAuthIntegrations.some(
      (integration) => integration.integrationType === "gmail",
    );
    const hasRevokedOutlook = revokedOAuthIntegrations.some(
      (integration) => integration.integrationType === "outlook",
    );

    const hasRevoked = hasRevokedGmail || hasRevokedOutlook;
    const emailType = hasRevokedGmail
      ? "gmail"
      : hasRevokedOutlook
        ? "outlook"
        : null;

    return { hasRevoked, emailType };
  }, [integrations, isLoading, team?.settings.support?.enabled]);

  useEffect(() => {
    if (canAuthenticateWithThirdParties && revokedIntegrationInfo.hasRevoked) {
      amplitude.track(PlatformEvents.PERMISSIONS_RECONNECT_EMAIL_VIEWED, {
        emailType: revokedIntegrationInfo.emailType,
      });
    }
  }, [
    canAuthenticateWithThirdParties,
    revokedIntegrationInfo.hasRevoked,
    revokedIntegrationInfo.emailType,
  ]);

  if (!canAuthenticateWithThirdParties) {
    return null;
  }

  if (!revokedIntegrationInfo.hasRevoked) {
    return null;
  }

  const handleReconnectClick = () => {
    amplitude.track(PlatformEvents.PERMISSIONS_RECONNECT_EMAIL_CLICKED);
    navigate("/settings/channels/supportEmail");
  };

  return {
    severity: ActionRequiredSeverity.Medium,
    item: {
      id: "revoked-email-permissions",
      type: "warning",
      message:
        "Your email provider has revoked Redo's permissions. To continue receiving customer emails, please reconnect your email.",
      actions: [
        {
          actionType: "button",
          hierarchy: "primary",
          text: "Reconnect email",
          onClick: handleReconnectClick,
        },
      ],
    },
  };
}
